import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Moves focus to the top of the page content after a route change.
 *
 * THE PROBLEM THIS SOLVES
 *
 * A real page load resets focus to the document. Client-side routing does not: the
 * URL and the content change while focus stays on whatever was clicked. For a
 * keyboard user the next Tab resumes from a link that no longer exists, and for a
 * screen reader nothing announces that anything happened — the buyer clicks a deal
 * and hears silence.
 *
 * WHY tabIndex={-1} AND NOT autofocus ON A HEADING
 *
 * Headings are not focusable, so focus() on one silently does nothing unless it is
 * given a tab stop. -1 makes it programmatically focusable WITHOUT adding it to the
 * tab order, which is what you want: focus lands there on navigation, and Tab from
 * there goes to the first real control rather than cycling through headings.
 *
 * The first render is skipped deliberately. On a fresh page load the browser has
 * already put focus in the right place, and stealing it would interrupt anyone who
 * started typing before the JavaScript finished.
 */
export function RouteFocus({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const target = useRef<HTMLDivElement>(null);
  const first = useRef(true);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }

    target.current?.focus();
  }, [location.pathname]);

  return (
    <div ref={target} tabIndex={-1} style={{ outline: 'none' }}>
      {children}
    </div>
  );
}
